import {
	getValidationTypeOrdersByGroupType,
	IDocumentVerificationValueSource,
	IFaceMatchSource,
	ValidationDocCountryCode,
	ValidationDocType,
	ValidationGroupType,
	ValidationType,
} from '@core/api/data-access';

import {ValidationGroupModel} from './validation-group.model';
import {ValidationModelFactory} from './validation.factory';

export const LivenessConditionType = {
	LivenessOnly: 'LivenessOnly',
	CompareImage: 'CompareImage',
	CompareFieldValue: 'CompareFieldValue',
	CompareWithValue: 'CompareWithValue',
	CompareMetaTagValue: 'CompareMetaTagValue',
} as const;
export type LivenessConditionType = (typeof LivenessConditionType)[keyof typeof LivenessConditionType];
/* -------------------------------------------------- */
//////
type OldCondition = {
	type: 'Liveness';
	subType?: LivenessConditionType;
	docTypes?: ValidationDocType[];
	countryCode?: ValidationDocCountryCode;
	key?: string;
	value?: string;
	index?: number;
};

type IOldLivenessOnly = OldCondition & {subType: 'LivenessOnly'};

function isLivenessOnly(condition: any): condition is IOldLivenessOnly {
	return condition.subType === 'LivenessOnly';
}

type IOldCompareImage = OldCondition & {subType: 'CompareImage'} & Pick<OldCondition, 'docTypes' | 'countryCode' | 'index'>;

function isCompareImage(condition: any): condition is IOldCompareImage {
	return condition.subType === 'CompareImage' && Array.isArray(condition.docTypes) && typeof condition.index === 'number';
}

type IOldCompareFieldValue = OldCondition & {subType: 'CompareFieldValue'} & Pick<OldCondition, 'docTypes' | 'countryCode'>;

function isCompareFieldValue(condition: any): condition is IOldCompareFieldValue {
	return condition.subType === 'CompareFieldValue' && Array.isArray(condition.docTypes);
}

type IOldCompareWithValue = OldCondition & {subType: 'CompareWithValue'} & Pick<OldCondition, 'docTypes' | 'countryCode' | 'value'>;

function isCompareWithValue(condition: any): condition is IOldCompareWithValue {
	return condition.subType === 'CompareWithValue' && Array.isArray(condition.docTypes) && typeof condition.value === 'string';
}

type IOldCompareMetaTagValue = OldCondition & {subType: 'CompareMetaTagValue'} & Pick<OldCondition, 'docTypes' | 'countryCode' | 'key'>;

function isCompareMetaTagValue(condition: any): condition is IOldCompareMetaTagValue {
	return condition.subType === 'CompareMetaTagValue' && Array.isArray(condition.docTypes) && typeof condition.key === 'string';
}

type OldConditionType = IOldLivenessOnly | IOldCompareImage | IOldCompareFieldValue | IOldCompareWithValue | IOldCompareMetaTagValue;

function isOldConditionType(condition: unknown): condition is OldConditionType {
	return isLivenessOnly(condition) || isCompareImage(condition) || isCompareFieldValue(condition) || isCompareWithValue(condition) || isCompareMetaTagValue(condition);
}

export function isOldConditionsArray(conditionsArray: string[]): conditionsArray is [string] {
	if (conditionsArray.length !== 1) {
		return false;
	}
	const oldCondition = JSON.parse(conditionsArray[0]);
	return isOldConditionType(oldCondition);
}

/* -------------------------------------------------------------------------------------------------------------- */
interface NewCondition {
	ordinal?: number;
	type: ValidationType;
	documentType?: ValidationDocType[];
	countryCodes?: ValidationDocCountryCode;
	valueToCompareSource?: IDocumentVerificationValueSource;
	imageToCompare?: IFaceMatchSource;
}

export function migrateConditionsArray(oldConditionsArray: string[]): ValidationGroupModel {
	if (!isOldConditionsArray(oldConditionsArray)) {
		return new ValidationGroupModel();
	}

	const oldCondition: OldCondition = JSON.parse(oldConditionsArray[0]);
	const groupType = getGroupTypeFromOldCondition(oldCondition);
	const validationTypeOrders = getValidationTypeOrdersByGroupType(groupType);
	const conditions: NewCondition[] = [
		{
			ordinal: validationTypeOrders[ValidationType.Liveness],
			type: ValidationType.Liveness,
		},
		...getAdditionalConditions(groupType, oldCondition, validationTypeOrders),
	];

	return new ValidationGroupModel({groupType, validations: conditions.map(ValidationModelFactory)});
}

function getGroupTypeFromOldCondition(oldCondition: OldCondition): ValidationGroupType {
	if (oldCondition.hasOwnProperty('subType')) {
		const subType = oldCondition.subType;
		switch (subType) {
			case 'CompareMetaTagValue':
				return ValidationGroupType.LivenessDocumentVerificationMetaTagValue;
			case 'CompareWithValue':
				return ValidationGroupType.LivenessDocumentVerificationStaticValue;
			case 'CompareImage':
				return ValidationGroupType.LivenessMatchMetadataImage;
			case 'CompareFieldValue':
				return ValidationGroupType.LivenessDocumentVerification;
			case 'LivenessOnly':
				return ValidationGroupType.Liveness;
			default:
				return null;
		}
	} else return oldCondition.type === 'Liveness' ? ValidationGroupType.Liveness : null;
}

function getAdditionalConditions(groupType: ValidationGroupType, oldCondition: OldCondition, validationTypeOrders: Record<ValidationType, number>): NewCondition[] {
	const additionalConditions: NewCondition[] = [];

	switch (groupType) {
		case ValidationGroupType.LivenessDocumentVerificationMetaTagValue:
			additionalConditions.push(
				{
					ordinal: validationTypeOrders[ValidationType.DocumentVerification],
					type: ValidationType.DocumentVerification,
					documentType: oldCondition.docTypes.map(type => type as ValidationDocType),
					countryCodes: (oldCondition.countryCode as ValidationDocCountryCode) || ValidationDocCountryCode.Any,
					valueToCompareSource: {metaTag: oldCondition.key},
				},
				{
					ordinal: validationTypeOrders[ValidationType.FaceMatch],
					type: ValidationType.FaceMatch,
					imageToCompare: {documentVerification: true},
				},
			);
			break;
		case ValidationGroupType.LivenessDocumentVerificationStaticValue:
			additionalConditions.push({
				ordinal: validationTypeOrders[ValidationType.DocumentVerification],
				type: ValidationType.DocumentVerification,
				documentType: oldCondition.docTypes.map(type => type as ValidationDocType),
				countryCodes: (oldCondition.countryCode as ValidationDocCountryCode) || ValidationDocCountryCode.Any,
				valueToCompareSource: {staticValue: oldCondition.value},
			});
			break;
		case ValidationGroupType.LivenessDocumentVerification:
			additionalConditions.push({
				ordinal: validationTypeOrders[ValidationType.DocumentVerification],
				type: ValidationType.DocumentVerification,
				documentType: oldCondition.docTypes.map(type => type as ValidationDocType),
				countryCodes: (oldCondition.countryCode as ValidationDocCountryCode) || ValidationDocCountryCode.Any,
			});
			break;
		case ValidationGroupType.LivenessMatchMetadataImage:
			additionalConditions.push({
				ordinal: validationTypeOrders[ValidationType.FaceMatch],
				type: ValidationType.FaceMatch,
				imageToCompare: {metaData: parseInt(String(oldCondition.index), 10)},
			});
			break;
		default:
			break;
	}

	return additionalConditions;
}
